// src/pages/Connector/GoogleDrive/EditGoogleDriveFileContent.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Box, Typography, Alert, CircularProgress, Stack, Divider, Button as MuiButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import TiptapEditor from "../../../components/Common/TiptapEditor";
import { Select } from 'antd';
import { marked } from 'marked'; 

const { Option } = Select;

export default function EditGoogleDriveFileContent({ document, onClose, onUpdate, chatbotId, permission, categories = []}) {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  const params = chatbotId ? { chatbot_id: chatbotId } : {};

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}), 
      Underline,
      Table.configure({ resizable: true }), 
      TableCell, 
      TableHeader, 
      TableRow,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: '_blank' } }),
    ],
    content: '',
  });

  useEffect(() => {
    if (editor && document) {
      const newContent = document.page_content 
        ? marked.parse(document.page_content) 
        : "<p></p>";
      
      if (editor.getHTML() !== newContent) {
        editor.commands.setContent(newContent, false);
      }
      const initialCategoryIds = document.categories?.map(cat => cat.id) || [];
      setSelectedCategories(initialCategoryIds);
    }
  }, [document, editor]);

  const handleSave = async () => {
    if (!editor || editor.isEmpty) {
        setError("Content cannot be empty.");
        return;
    }
    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = { 
        id: document.id, 
        page_content: editor.getHTML(),
        category_ids: selectedCategories
      };
      const response = await axiosInstance.post(`/api/google-drive/update-file-content/${document.id}`, payload, { params });
      if (response.status === 200) {
        setSuccess("Content and categories updated successfully.");
        setTimeout(() => onUpdate(), 1200);
      } else {
         setError(`Failed to update data (Status: ${response.status})`);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update data.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!editor) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress /><Typography ml={2}>Loading Editor...</Typography></Box>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ p: 2, flexShrink: 0 }}>
              <Typography variant="h6">Edit Google Drive File Content</Typography>
              <Typography variant="body2" color="text.secondary">{document?.filename}</Typography>
          </Box>
          <Divider />

          {/* Scrolling Content Area */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}
              
              {/* Category Selector */}
              <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Categories</Typography>
                  <Select 
                      mode="multiple" 
                      allowClear 
                      style={{ width: '100%' }} 
                      placeholder="Select categories or leave blank for 'Uncategorized'" 
                      onChange={setSelectedCategories} 
                      value={selectedCategories} 
                      disabled={isSaving}
                  >
                      {categories.map(cat => <Option key={cat.id} value={cat.id}>{cat.name}</Option>)}
                  </Select>
              </Box>
              
              {/* Tiptap Editor */}
              <TiptapEditor editor={editor} height="350px" />
          </Box>

          {/* Footer with Buttons - pinned to bottom via flexShrink: 0 */}
          <Divider />
          <Stack direction="row" spacing={2} sx={{ p: 2, flexShrink: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              {permission.canModify && (
                <MuiButton 
                  variant="contained" 
                  onClick={handleSave} 
                  disabled={isSaving} 
                  startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />} 
                  fullWidth
                >
                  {isSaving ? 'Saving...' : 'Save Content'}
                </MuiButton>
            )}
              <MuiButton 
                variant="outlined" 
                color="secondary" 
                onClick={onClose} 
                disabled={isSaving} 
                fullWidth
              >
                  Cancel
              </MuiButton>
          </Stack>
      </Box>
      
      {/* Style block to ensure Ant Design dropdown appears above the MUI modal */}
      <style jsx global>{`
          .ant-select-dropdown { z-index: 1500 !important; }
      `}</style>
    </>
  );
}